<template lang="pug">
.author-editor-tabs.flex.bg-gray-100.flex-none.dark_bg-stone-700.shadow-inner.duration-500.h-12.items-center
  span.h-full.px-4.py-2.border-b-sky-600.border-0.border-b-4.text-base.me-auto.flex.items-center.justify-center.bg-white.bg-opacity-50.dark_bg-opacity-50.flex-0.dark_bg-gray-800.dark_text-white.border-e(
    class="border-e-black/10"
  )
    span {{ filename }}

  .ms-auto

  button.btn.ripple.ripple--centered.py-1.px-2.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.w-8.h-8(
    @click="quickCommand"
    aria-label="Quick Command... (F1)"
    title="Quick Command... (F1)"
  )
    Icon(icon="ph:dots-three-vertical-bold")
  button.btn.ripple.ripple--centered.py-1.px-2.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.w-8.h-8(
    @click="format"
    aria-label="Format (F2)"
    title="Tidy formatting (F2)"
  ) 
    Icon(icon="ph:broom")
  button.btn.ripple.ripple--centered.py-1.px-2.opacity-70.hover_opacity-100.dark_bg-opacity-0.bg-opacity-0.hover_bg-opacity-80.w-8.h-8(
    @click="find"
    aria-label="Find... (F3)"
    title="Find... (F3)"
  ) 
    Icon(icon="ph:magnifying-glass")
.author-monaco.flex-1.w-full.min-h-0(ref="elRef")
</template>

<script setup lang="ts">
import { editor } from 'monaco-editor';
import * as monaco from 'monaco-editor';
import { createFileModel } from '../store/useMonacoEditors';
import { onUnmounted, ref, watch, provide } from 'vue';
import useEditorOptions from '../store/useEditorOptions';
import { useEventListener } from '@vueuse/core';
import useDark from '../store/useDark';
import type AuthorMonacoProps from '../../../types/AuthorMonacoProps.d.ts';
import { Icon } from '@iconify/vue';
import gsap from 'gsap';
import upsertPatch from '../../edit-agent/lib/upsertPatch';
import upsertBase from '../../edit-agent/lib/upsertBase';
import revertPatchFn from '../../edit-agent/lib/revertPatch';
import { updateEditorSchema } from '../lib/monacoEnv';
import { CourseType } from '../lib/schemaRegistry';
import jsYaml from 'js-yaml';

const props = defineProps<AuthorMonacoProps>();

let ed: editor.IStandaloneCodeEditor | null = null;
let disposables: monaco.IDisposable[] = [];

const dispose = () => {
  ed?.dispose();
  ed = null;
  disposables.forEach(d => d.dispose());
  disposables = [];
};

onUnmounted(dispose);

const elRef = ref<HTMLElement>();
const { wordWrap, toggleWordWrap } = useEditorOptions();
const dark = useDark();

watch(
  elRef,
  el => {
    if (!el) return;
    dispose();
    const model = createFileModel(props);

    ed = editor.create(el, {
      // Headless instances are never visible, so the ResizeObserver-based layout
      // polling is pure waste.
      automaticLayout: !props.headless,
      scrollBeyondLastLine: false,
      mouseWheelZoom: true,
      stickyScroll: { enabled: true, maxLineCount: 4 },
      tabSize: 2,
      model
    });

    ed.updateOptions({ theme: dark.value ? 'uc-dark' : 'uc-light' });
    ed.updateOptions({ wordWrap: wordWrap.value ? 'on' : 'off' });
    ed.updateOptions({ readOnly: props.readOnly });

    // alt-z to toggle word wrap
    const wordWrapAction = ed.addAction({
      keybindings: [monaco.KeyMod.Alt | monaco.KeyCode.KeyZ],
      run: toggleWordWrap,
      label: 'Toggle Word Wrap',
      id: 'toggle-wrap',
      contextMenuGroupId: 'navigation',
      contextMenuOrder: 1.5
    });

    disposables.push(wordWrapAction);

    const onChange = () => props.onChange(ed?.getValue());
    const changeListener = model.onDidChangeContent(onChange);
    disposables.push(changeListener);
  },
  { immediate: true }
);

watch(dark, v => ed && ed.updateOptions({ theme: v ? 'uc-dark' : 'uc-light' }));

watch(elRef, async el => {
  // No fade for an invisible editor.
  if (props.headless) return;
  const canvases = el?.querySelectorAll('.author-monaco canvas');

  gsap.fromTo(
    [canvases],
    { opacity: 0 },
    {
      opacity: 1,
      ease: 'power1.inOut',
      overwrite: true,
      delay: 0.2,
      duration: 0.4
    }
  );
});

watch(wordWrap, w => ed?.updateOptions({ wordWrap: w ? 'bounded' : 'off' }));

watch(
  () => props.readOnly,
  r => ed?.updateOptions({ readOnly: r })
);

watch(
  () => props.activityType,
  newType => {
    // Headless buffer never surfaces validation errors, so don't spin up the
    // YAML schema-validation worker (the heaviest per-change runtime cost).
    if (props.headless) return;
    if (
      newType &&
      (newType === CourseType.FullCourse ||
        newType === CourseType.ConversationalLearning)
    ) {
      updateEditorSchema(newType);
    }
  },
  { immediate: true }
);

const find = () => ed?.getAction('actions.findWithSelection')?.run();
const format = () => ed?.getAction('editor.action.formatDocument')?.run();
const quickCommand = () => {
  ed?.focus();
  ed?.getAction('editor.action.quickCommand')?.run();
};

const onKeydown = (e: KeyboardEvent) => {
  const handlers: [string, () => void][] = e.altKey
    ? []
    : [
        ['F1', quickCommand],
        ['F2', format],
        ['F3', find]
      ];

  for (const [key, callback] of handlers) {
    if (e.key === key) {
      e.preventDefault();
      callback();
      return;
    }
  }
};

useEventListener(elRef, 'keydown', onKeydown);

// -------------------- Atomic acceptance helper --------------------
function diffOffsets(before: string, after: string) {
  if (before === after) return null;

  let start = 0;
  const min = Math.min(before.length, after.length);
  while (start < min && before.charCodeAt(start) === after.charCodeAt(start))
    start++;

  let endBefore = before.length;
  let endAfter = after.length;
  while (
    endBefore > start &&
    endAfter > start &&
    before.charCodeAt(endBefore - 1) === after.charCodeAt(endAfter - 1)
  ) {
    endBefore--;
    endAfter--;
  }

  return { startAfter: start, endAfter };
}

function applyAtomic(
  screenYaml: string,
  transform: (courseYaml: string, screenYaml: string) => string,
  opId: string,
  swallowErrors = false
) {
  if (!ed) return;
  const model = ed.getModel();
  if (!model) return;

  const before = model.getValue();

  let after: string;
  try {
    after = transform(before, screenYaml);
  } catch (e) {
    console.error(`${opId} failed:`, e);
    if (swallowErrors) return;
    throw e;
  }

  if (after === before) return;

  // compute where the change lands in the *after* text
  const diff = diffOffsets(before, after);

  ed.pushUndoStop();
  ed.executeEdits(opId, [{ range: model.getFullModelRange(), text: after }]);
  ed.pushUndoStop();

  // Nothing visible to scroll to in a headless buffer.
  if (props.headless || !diff) return;

  const m2 = ed.getModel();
  if (!m2) return;

  const startPos = m2.getPositionAt(diff.startAfter);
  const endPos = m2.getPositionAt(Math.max(diff.endAfter, diff.startAfter));

  const range = new monaco.Range(
    startPos.lineNumber,
    startPos.column,
    endPos.lineNumber,
    endPos.column
  );

  ed.revealRangeInCenterIfOutsideViewport(
    range,
    monaco.editor.ScrollType.Smooth
  );
  // ed.setSelection(range); // optional; remove if you only want scroll
  // ed.focus();

  ed.render(true);
}

// -------------------- Patch acceptance --------------------

function acceptPatch(screenYaml: string) {
  applyAtomic(screenYaml, upsertPatch, 'ai-accept-patch', true);
}

// -------------------- Base acceptance --------------------

function acceptBase(screenYaml: string) {
  applyAtomic(screenYaml, upsertBase, 'ai-accept-base', false);
}

function acceptPath(path: string, value: string) {
  if (!path) return;

  applyAtomic(
    value,
    (courseYaml, patchValue) => {
      const parsed = (jsYaml.load(courseYaml || '') as any) || {};
      parsed.patches = parsed.patches || {};
      parsed.patches[path] = [{ value: patchValue }];
      return jsYaml.dump(parsed, { lineWidth: -1 });
    },
    'ai-accept-path',
    false
  );
}

// Toggle an include/exclude entry. `hidden` is a top-level map keyed by id, not
// a patch, so it can't go through acceptPath (which writes to `patches`).
function acceptHidden(id: string, hidden: boolean) {
  if (!id) return;

  applyAtomic(
    '',
    courseYaml => {
      const parsed = (jsYaml.load(courseYaml || '') as any) || {};
      parsed.hidden = parsed.hidden || {};
      parsed.hidden[id] = hidden;
      return jsYaml.dump(parsed, { lineWidth: -1 });
    },
    'accept-hidden',
    false
  );
}

// Replace the whole top-level `patches` map (e.g. when adding a page from the
// preview). `patchesJson` is a JSON string of the full patches object.
function acceptPatches(patchesJson: string) {
  if (!patchesJson) return;

  applyAtomic(
    '',
    courseYaml => {
      const parsed = (jsYaml.load(courseYaml || '') as any) || {};
      parsed.patches = JSON.parse(patchesJson);
      const out = jsYaml.dump(parsed, { lineWidth: -1 });
      console.log('[ADD-PAGE monaco]', performance.now().toFixed(0), {
        patchKeys: Object.keys(parsed.patches || {}),
        beforeLen: courseYaml?.length,
        afterLen: out.length
      });
      return out;
    },
    'accept-patches',
    false
  );
}

// -------------------- Patch revert --------------------

function revertPatch(screenId: string) {
  if (!screenId) {
    throw new Error('revertPatch requires screenId');
  }

  if (!ed) return;
  const model = ed.getModel();
  if (!model) return;

  const before = model.getValue();

  let after: string;
  try {
    after = revertPatchFn(before, screenId);
  } catch (e) {
    console.error('revertPatch failed:', e);
    // Swallow error - if screen not found, it's already gone
    return;
  }

  if (after === before) {
    console.log('No changes after revert (screen not in patches)');
    return; // no-op
  }

  ed.pushUndoStop();
  ed.executeEdits('ai-revert-patch', [
    { range: model.getFullModelRange(), text: after }
  ]);
  ed.pushUndoStop();

  console.log('✅ Patch reverted from Monaco editor');
}

// Expose to parent and provide downstream via Vue provide/inject
defineExpose({
  acceptPatch,
  acceptBase,
  acceptPath,
  acceptHidden,
  acceptPatches,
  revertPatch
});
provide('acceptPatch', acceptPatch);
</script>
